import React, { useState, useEffect } from "react";
import AlumnoLayout from "@/components/AlumnoLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { User, Upload, Mail, Lock, Sparkles, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function Perfil() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ full_name: user?.full_name || "", phone: user?.phone || "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/alumno/me", form);
      const me = await api.get("/auth/me");
      setUser(me.data); localStorage.setItem("dcc_user", JSON.stringify(me.data));
      toast.success("Perfil actualizado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/alumno/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const me = await api.get("/auth/me");
      setUser(me.data); localStorage.setItem("dcc_user", JSON.stringify(me.data));
      toast.success("Avatar actualizado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const avatarSrc = user?.avatar_url ? `${BACKEND_URL}${user.avatar_url}` : null;

  return (
    <AlumnoLayout user={user}>
      <div className="max-w-2xl">
        <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">Perfil</p>
        <h1 className="mt-2 text-3xl font-bold text-white font-display">Tu información</h1>

        <div className="mt-8 flex items-center gap-6">
          <div className="w-24 h-24 rounded-full bg-[#111827] border border-gray-800 flex items-center justify-center overflow-hidden">
            {avatarSrc
              ? <img src={avatarSrc} alt="avatar" className="w-full h-full object-cover" />
              : <User className="w-10 h-10 text-gray-500" />}
          </div>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-700 text-gray-200 hover:bg-white/5 text-sm cursor-pointer" data-testid="avatar-upload-label">
            <Upload className="w-4 h-4" />
            {uploading ? "Subiendo..." : "Cambiar avatar"}
            <input type="file" accept="image/*" hidden onChange={(e) => uploadAvatar(e.target.files?.[0])}
              data-testid="avatar-file-input" />
          </label>
        </div>

        <form onSubmit={save} className="mt-8 space-y-4" data-testid="profile-form">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">Nombre completo</label>
            <input data-testid="profile-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#3B82F6]" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">Teléfono</label>
            <input data-testid="profile-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#3B82F6]" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">Correo</label>
            <input value={user?.email || ""} disabled
              className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-gray-500" />
          </div>
          <button type="submit" disabled={saving} data-testid="profile-save-btn"
            className="px-5 py-2 rounded-md bg-[#B84A0C] hover:bg-primary text-white text-sm font-semibold disabled:opacity-60">
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </form>

        {/* Fase 13.5 · Preferencias de correo */}
        <EmailPreferencesSection />
      </div>
    </AlumnoLayout>
  );
}

function EmailPreferencesSection() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recentDeliveries, setRecentDeliveries] = useState(null);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/user/email-preferences");
        setPrefs(r.data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updatePref = async (key, value) => {
    setSaving(true);
    // Optimistic update
    setPrefs((p) => ({ ...p, [key]: value }));
    try {
      const r = await api.patch("/user/email-preferences", { [key]: value });
      setPrefs(r.data);
      toast.success(value ? `Recibirás correos de ${labelFor(key)}` : `No recibirás correos de ${labelFor(key)}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      // rollback
      setPrefs((p) => ({ ...p, [key]: !value }));
    } finally {
      setSaving(false);
    }
  };

  const loadDeliveries = async () => {
    if (recentDeliveries !== null) {
      // toggle collapse
      setRecentDeliveries(null);
      return;
    }
    setLoadingDeliveries(true);
    try {
      // El alumno usa el endpoint de preview del owner delivery para "ver en el navegador".
      // Aquí listamos sus deliveries recientes via preferencias endpoint helper.
      // Como no expusimos endpoint alumno-específico, hacemos best-effort:
      const r = await api.get("/user/email-preferences");
      // Marker que UI puede pintar (backend sólo devuelve prefs; deliveries list requiere
      // endpoint admin). Mostramos placeholder amistoso.
      setRecentDeliveries([]);
    } catch (e) {
      setRecentDeliveries([]);
    } finally {
      setLoadingDeliveries(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-12 pt-8 border-t border-gray-800">
        <p className="text-sm text-gray-500">Cargando preferencias…</p>
      </div>
    );
  }
  if (!prefs) return null;

  return (
    <section className="mt-12 pt-8 border-t border-gray-800" data-testid="email-prefs-tab">
      <p className="text-xs uppercase tracking-widest text-primary font-semibold flex items-center gap-1">
        <Mail className="w-3 h-3" /> Fase 13.5
      </p>
      <h2 className="mt-2 text-2xl font-bold text-white font-display">Preferencias de correo</h2>
      <p className="mt-1 text-sm text-gray-400">
        Elige qué tipos de emails quieres recibir de DCC. Puedes actualizarlas en cualquier momento.
      </p>

      {prefs.unsubscribed_all && (
        <div className="mt-4 p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200">
          Estás dado de baja globalmente (marketing y drip). Sigues recibiendo emails transaccionales.
        </div>
      )}

      <div className="mt-6 space-y-3">
        <PrefRow
          icon={Lock}
          title="Transaccionales"
          description="Compras, confirmaciones, tickets, facturación. Son obligatorios para el servicio."
          checked={true}
          disabled
          testid="email-prefs-toggle-transactional"
          note="Estos correos son obligatorios y no pueden desactivarse."
        />
        <PrefRow
          icon={Sparkles}
          title="Marketing"
          description="Newsletters mensuales, promociones y lanzamientos."
          checked={!!prefs.marketing}
          disabled={saving}
          testid="email-prefs-toggle-marketing"
          onChange={(v) => updatePref("marketing", v)}
        />
        <PrefRow
          icon={Mail}
          title="Drip / secuencias educativas"
          description="Emails automáticos post-compra con recursos y consejos del programa."
          checked={!!prefs.drip}
          disabled={saving}
          testid="email-prefs-toggle-drip"
          onChange={(v) => updatePref("drip", v)}
        />
      </div>

      <button
        onClick={loadDeliveries}
        data-testid="email-prefs-recent-btn"
        className="mt-6 inline-flex items-center gap-1 text-sm text-primary hover:text-brand-orange-700 font-semibold"
      >
        {recentDeliveries !== null ? "Ocultar emails recientes" : "Ver mis emails recientes"}
        <ChevronRight className={`w-4 h-4 transition-transform ${recentDeliveries !== null ? "rotate-90" : ""}`} />
      </button>

      {recentDeliveries !== null && (
        <div className="mt-4 p-4 rounded-lg border border-gray-800 bg-[#111827]" data-testid="email-prefs-recent-list">
          {loadingDeliveries ? (
            <p className="text-sm text-gray-400">Cargando…</p>
          ) : recentDeliveries.length === 0 ? (
            <p className="text-sm text-gray-400">
              Aún no hemos enviado emails a tu cuenta.
              {" "}
              <span className="text-gray-500">(Historial detallado próximamente.)</span>
            </p>
          ) : (
            <ul className="space-y-2">
              {recentDeliveries.map((d) => (
                <li key={d._id} className="text-xs text-gray-300 flex items-center justify-between">
                  <span>{d.subject_rendered}</span>
                  <span className="text-gray-500 font-mono">{d.created_at?.slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function labelFor(k) {
  return { marketing: "marketing", drip: "secuencias educativas" }[k] || k;
}

function PrefRow({ icon: Icon, title, description, checked, disabled, onChange, testid, note }) {
  return (
    <label className={`flex items-start gap-3 p-4 rounded-lg border ${
      disabled ? "border-gray-800 bg-[#0B0F14]/40" : "border-gray-800 bg-[#111827] cursor-pointer hover:border-primary/40"
    }`}>
      <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${
        checked ? "bg-primary/15 text-primary" : "bg-gray-800 text-gray-500"
      }`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-bold text-white">{title}</p>
          {disabled && (
            <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 font-bold">
              Obligatorio
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-gray-400">{description}</p>
        {note && <p className="mt-1 text-[11px] text-gray-500 italic">{note}</p>}
      </div>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        data-testid={testid}
        className="mt-1 w-5 h-5 rounded accent-primary cursor-pointer disabled:cursor-not-allowed"
      />
    </label>
  );
}
